import { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Save, X, Code, Eye, ChevronRight, ChevronDown, Search, XCircle } from 'lucide-react';
import { EmailTemplate, CreateEmailTemplateData, UpdateEmailTemplateData, TEMPLATE_VARIABLES } from '@/types/emailTemplate';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';

// Register Quill modules only once
if (!ReactQuill.Quill.imports['modules/imageResize']) {
  ReactQuill.Quill.register('modules/imageResize', ImageResize);
}
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { TemplatePreviewService, PreviewData } from '@/services/templatePreviewService';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: EmailTemplate | null;
  matchId?: string; // Optional match ID for context-specific previews
}

export const EmailTemplateModal = ({ isOpen, onClose, template, matchId }: EmailTemplateModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { createTemplate, updateTemplate } = useEmailTemplates();
  const quillRef = useRef<ReactQuill>(null);

  const [formData, setFormData] = useState<CreateEmailTemplateData>({
    name: '',
    subject: '',
    content: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Candidate', 'Job', 'Company']));
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [fieldFilter, setFieldFilter] = useState('');

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (isOpen) {
      if (template) {
        setFormData({
          name: template.name,
          subject: template.subject,
          content: template.content,
          description: template.description || '',
        });
      } else {
        setFormData({
          name: '',
          subject: '',
          content: '',
          description: '',
        });
      }
      setViewMode('edit');
    }
  }, [isOpen, template]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.name.trim() || !formData.subject.trim() || !formData.content.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields (name, subject, and content).",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (template) {
        await updateTemplate(template.id, formData);
      } else {
        await createTemplate(formData);
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const insertVariable = (variableKey: string) => {
    const placeholder = `{{${variableKey}}}`;
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      quill.insertText(index, placeholder);
      quill.setSelection(index + placeholder.length);
    }
  };

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const loadPreviewData = async () => {
    if (viewMode === 'preview' && !previewData) {
      setLoadingPreview(true);
      try {
        const data = await TemplatePreviewService.fetchPreviewData(matchId);
        setPreviewData(data);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load preview data",
          variant: "destructive",
        });
      } finally {
        setLoadingPreview(false);
      }
    }
  };

  // Load preview data when switching to preview mode
  useEffect(() => {
    if (viewMode === 'preview') {
      loadPreviewData();
    }
  }, [viewMode]);

  const getPreviewContent = () => {
    if (!previewData) return formData.content;
    return TemplatePreviewService.replaceTemplateVariables(formData.content, previewData);
  };

  const getPreviewSubject = () => {
    if (!previewData) return formData.subject;
    return TemplatePreviewService.replaceTemplateVariables(formData.subject, previewData);
  };

  // Filter template variables based on search term using useMemo
  const filteredTemplateVariables = useMemo(() => {
    if (!fieldFilter) return TEMPLATE_VARIABLES;
    
    return TEMPLATE_VARIABLES.map(category => ({
      ...category,
      variables: category.variables.filter(variable => {
        const searchTerm = fieldFilter.toLowerCase();
        return (
          variable.key.toLowerCase().includes(searchTerm) ||
          variable.label.toLowerCase().includes(searchTerm) ||
          variable.description.toLowerCase().includes(searchTerm) ||
          variable.example.toLowerCase().includes(searchTerm) ||
          category.name.toLowerCase().includes(searchTerm)
        );
      })
    })).filter(category => category.variables.length > 0);
  }, [fieldFilter]);

  // Auto-expand categories when filtering
  useEffect(() => {
    if (fieldFilter) {
      // Expand all categories that have matching variables
      const categoriesToExpand = filteredTemplateVariables.map(cat => cat.name);
      setExpandedCategories(new Set(categoriesToExpand));
    }
  }, [fieldFilter, filteredTemplateVariables]);

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean']
    ],
    imageResize: {
      parchment: ReactQuill.Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    },
    clipboard: {
      // Enable HTML pasting
      matchVisual: false
    }
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'align', 'color', 'background',
    'script', 'super', 'sub',
    'code-block',
    'direction'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-[90vw] h-[90vh] p-0 flex flex-col" aria-describedby="email-template-description">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle>
              {template 
                ? t('admin.emailTemplates.editTemplate', 'Edit Email Template')
                : t('admin.emailTemplates.createTemplate', 'Create Email Template')
              }
            </DialogTitle>
            <div id="email-template-description" className="sr-only">
              {template 
                ? 'Edit an existing email template with rich text formatting, images, and dynamic field insertion.'
                : 'Create a new email template with rich text formatting, images, and dynamic field insertion.'
              }
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex overflow-hidden">
          {/* Main Content */}
          <div className="flex-1 flex flex-col p-6 overflow-hidden">
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4 overflow-hidden">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('admin.emailTemplates.name', 'Template Name')} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('admin.emailTemplates.namePlaceholder', 'Enter template name')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">{t('admin.emailTemplates.subject', 'Email Subject')} *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder={t('admin.emailTemplates.subjectPlaceholder', 'Enter email subject')}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('admin.emailTemplates.description', 'Description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('admin.emailTemplates.descriptionPlaceholder', 'Optional description for this template')}
                  disabled={isSubmitting}
                  rows={2}
                />
              </div>

              {/* Content Editor */}
              <div className="flex-1 flex flex-col space-y-2 overflow-hidden">
                <div className="flex items-center justify-between">
                  <Label>{t('admin.emailTemplates.content', 'Email Content')} *</Label>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Edit</span>
                    </div>
                    <Switch
                      checked={viewMode === 'preview'}
                      onCheckedChange={(checked) => setViewMode(checked ? 'preview' : 'edit')}
                      disabled={isSubmitting}
                    />
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Preview</span>
                    </div>
                  </div>
                </div>
                {viewMode === 'edit' ? (
                  <div className="flex-1 overflow-hidden">
                    <ReactQuill
                      ref={quillRef}
                      value={formData.content}
                      onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={t('admin.emailTemplates.contentPlaceholder', 'Enter your email content here. You can insert images and rich formatting for professional email templates...')}
                      style={{ height: '100%' }}
                      className="h-full email-template-editor"
                      theme="snow"
                    />
                    <style jsx global>{`
                      .email-template-editor .ql-toolbar {
                        border-top: 1px solid #e5e7eb;
                        border-left: 1px solid #e5e7eb;
                        border-right: 1px solid #e5e7eb;
                        background-color: #f9fafb;
                      }
                      .email-template-editor .ql-container {
                        border-bottom: 1px solid #e5e7eb;
                        border-left: 1px solid #e5e7eb;
                        border-right: 1px solid #e5e7eb;
                        font-size: 14px;
                      }
                      .email-template-editor .ql-editor {
                        min-height: 300px;
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        padding: 12px 15px;
                      }
                      .email-template-editor .ql-toolbar .ql-formats {
                        margin-right: 15px;
                      }
                      .email-template-editor img {
                        max-width: 100%;
                        height: auto;
                        display: block;
                        margin: 10px 0;
                      }
                      .email-template-editor .ql-editor.ql-blank::before {
                        font-style: italic;
                        color: #9ca3af;
                      }
                      /* Hide deprecated warnings in console */
                      .email-template-editor .ql-tooltip {
                        z-index: 1000;
                      }
                    `}</style>
                  </div>
                ) : (
                  <Card className="flex-1 overflow-hidden">
                    <CardContent className="p-4 h-full overflow-auto">
                      {loadingPreview ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-digger-blue"></div>
                          <span className="ml-2 text-muted-foreground">Loading preview...</span>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Preview Subject */}
                          <div className="border-b pb-4">
                            <h3 className="text-sm font-medium text-muted-foreground mb-2">Subject Line:</h3>
                            <p className="font-semibold text-lg">{getPreviewSubject()}</p>
                          </div>
                          
                          {/* Preview Content */}
                          <div className="space-y-2">
                            <h3 className="text-sm font-medium text-muted-foreground">Email Content:</h3>
                            <div 
                              className="prose max-w-none"
                              dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                            />
                          </div>
                          
                          {/* Preview Data Info */}
                          <div className="mt-6 pt-4 border-t">
                            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                              <strong>Preview Note:</strong> This preview uses real data from your database when available, 
                              or sample data as fallback. The actual email will be populated with data specific to the recipient.
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  <X className="h-4 w-4 mr-1" />
                  {t('admin.emailTemplates.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting} className="bg-digger-blue hover:bg-digger-blue-light">
                  <Save className="h-4 w-4 mr-1" />
                  {isSubmitting 
                    ? t('admin.emailTemplates.saving', 'Saving...') 
                    : (template 
                      ? t('admin.emailTemplates.update', 'Update Template')
                      : t('admin.emailTemplates.create', 'Create Template')
                    )
                  }
                </Button>
              </div>
            </form>
          </div>

          {/* Variables Sidebar */}
          <div className="w-80 border-l bg-muted/50 flex flex-col">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-sm mb-3">
                {t('admin.emailTemplates.availableFields', 'Available Fields')}
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search fields..."
                  value={fieldFilter}
                  onChange={(e) => setFieldFilter(e.target.value)}
                  className="pl-10 pr-8 h-8 text-sm"
                />
                {fieldFilter && (
                  <button
                    type="button"
                    onClick={() => setFieldFilter('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.emailTemplates.clickToInsert', 'Click any field to insert it into your template')}
              </p>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-2">
                {fieldFilter && filteredTemplateVariables.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No fields found matching "{fieldFilter}"</p>
                    <p className="text-xs mt-1">Try adjusting your search terms</p>
                  </div>
                ) : (
                  (fieldFilter ? filteredTemplateVariables : TEMPLATE_VARIABLES).map((category) => (
                    <Card key={category.name} className="overflow-hidden">
                      <Collapsible 
                        open={expandedCategories.has(category.name)}
                        onOpenChange={() => toggleCategory(category.name)}
                      >
                        <CollapsibleTrigger asChild>
                          <CardHeader className="p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm font-medium">
                                {category.name}
                                {fieldFilter && (
                                  <Badge variant="secondary" className="ml-2 text-xs">
                                    {category.variables.length}
                                  </Badge>
                                )}
                              </CardTitle>
                              {expandedCategories.has(category.name) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <CardContent className="p-0 pt-0">
                            <div className="space-y-1">
                              {category.variables.map((variable) => (
                              <button
                                key={variable.key}
                                type="button"
                                onClick={() => insertVariable(variable.key)}
                                className="w-full text-left p-2 hover:bg-muted/50 rounded-sm transition-colors group"
                                disabled={isSubmitting}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-mono text-xs text-blue-500 dark:text-blue-400 truncate">
                                      {`{{${variable.key}}}`}
                                    </div>
                                    <div className="text-xs font-medium truncate">{variable.label}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                      {variable.description}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1 italic">
                                  {t('admin.emailTemplates.example', 'Example')}: {variable.example}
                                </div>
                              </button>
                            ))}
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                ))
              )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
